import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  DropContent,
  Option,
  OptionKey,
  RawButton,
  Text,
  useClickOutside,
} from '@common-fe/common-fe';

interface Props {
  options: Option[];
  value: OptionKey;
  onChangeValue: (value: OptionKey) => void;
  width?: string;
}

const SubstantiationRateDropdown: React.FC<Props> = ({ options, value, onChangeValue, width = '72px' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);

  const currentTitle = useMemo(() => {
    const foundItem = options.find((item) => item.key === value);
    return (foundItem?.title || foundItem?.value || 'Select') as string;
  }, [options, value]);

  const handleToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  useClickOutside(ref, () => {
    setIsExpanded(false);
  });

  const handleClick = useCallback(
    (newValue: OptionKey) => {
      onChangeValue(newValue);
      setIsExpanded(false);
    },
    [onChangeValue]
  );

  return (
    <Box
      ref={ref}
      data-testid="dashboard_statistics_substantiation-rate_dropdown"
      style={{ position: 'relative' }}
    >
      <DropContent
        contentWidth={width}
        bottomMode
        content={
          <Box data-testid="dashboard_statistics_substantiation-rate_dropdown_content">
            {options.map((item) => (
              <RawButton
                key={item.key}
                onClick={() => handleClick(item.key)}
                data-testid={`dashboard_statistics_substantiation-rate_dropdown_option-${item.key}`}
              >
                <Box align="center" pad={{ vertical: 'spacing4' }}>
                  <Text size="medium" weight={item.key === value ? 700 : 400}>
                    {(item?.value || item?.title) as string}
                  </Text>
                </Box>
              </RawButton>
            ))}
          </Box>
        }
        visible={isExpanded}
        tailless
      >
        <RawButton
          data-testid="dashboard_statistics_substantiation-rate_dropdown_button"
          onClick={handleToggle}
        >
          <Box align="center" direction="row">
            <Text
              data-testid="dashboard_statistics_substantiation-rate_dropdown_text"
              size="medium"
              weight={400}
            >
              {currentTitle}
            </Text>
            {isExpanded ? (
              <ChevronUpIcon
                data-testid="dashboard_statistics_substantiation-rate_dropdown_chevron-up"
                size="24px"
                color="iconPrimary"
              />
            ) : (
              <ChevronDownIcon
                data-testid="dashboard_statistics_substantiation-rate_dropdown_chevron-down"
                size="24px"
                color="iconPrimary"
              />
            )}
          </Box>
        </RawButton>
      </DropContent>
    </Box>
  );
};

export default SubstantiationRateDropdown;
